import { ApiSchema } from "@api-types";
import { useObservable } from "@rsm";
import { combineLatest, filter, map } from "rxjs";
import { getClientService } from "services/client.service";
import { getUserService } from "services/user.service";

/** HubSpot company `type` value that marks an account as churned. */
export const PAST_CUSTOMER_HUBSPOT_TYPE = "Past Customer";

const isChurned = (client: ApiSchema.ClientsView) =>
  client.hubspotCompanies?.type === PAST_CUSTOMER_HUBSPOT_TYPE;

const isRestricted = (client: ApiSchema.ClientsView) => isChurned(client) && client.overrideChurn !== true;

export interface ChurnState {
  /** True when the account's HubSpot type is "Past Customer". */
  isChurned: boolean;
  /** True when the account is churned and no super-admin override is in place. Drives banner + UI block. */
  isRestricted: boolean;
  /** True when the logged-in user is an internal Ideally admin (bypasses the block, still sees the banner). */
  isIdeallyAdmin: boolean;
}

export const useIsChurned = (): ChurnState => {
  const client = useObservable(getClientService().client$, null);
  const user = useObservable(getUserService().user$, null);

  return {
    isChurned: !!client && isChurned(client),
    isRestricted: !!client && isRestricted(client),
    isIdeallyAdmin: user?.isIdeallyAdmin === true,
  };
};

/**
 * Emits true when the current route should be blocked because the account is a restricted
 * (churned, no override) customer and the user is not an Ideally admin. Ideally admins are never
 * blocked. Waits for BOTH client and user to resolve before emitting so route guards don't flash
 * allowed content on the first (null, null) tick from the seeded BehaviorSubjects.
 */
export const churnBlocked$ = combineLatest([getClientService().client$, getUserService().user$]).pipe(
  filter(([client, user]) => !!client && !!user),
  map(([client, user]) => isRestricted(client!) && user!.isIdeallyAdmin !== true),
);
